export interface NewsletterSeriesPost {
  id: string;
  title: string;
  publishedAt: string | null;
  isPinned?: boolean;
  tags?: string[] | null;
}

const SERIES_KEY_PREFIX = 'newsletter-series:';
const SERIES_NAME_PREFIX = 'newsletter-series-name:';

function normalizeTag(tag: string): string {
  return tag.trim();
}

function fallbackSeriesKey(post: NewsletterSeriesPost): string {
  return `issue:${post.id}`;
}

export function getNewsletterSeriesKey(post: NewsletterSeriesPost): string {
  const seriesTag = post.tags?.find(tag => normalizeTag(tag).startsWith(SERIES_KEY_PREFIX));
  return seriesTag
    ? normalizeTag(seriesTag).slice(SERIES_KEY_PREFIX.length)
    : fallbackSeriesKey(post);
}

export function getNewsletterSeriesName(post: NewsletterSeriesPost): string {
  const seriesNameTag = post.tags?.find(tag => normalizeTag(tag).startsWith(SERIES_NAME_PREFIX));
  if (seriesNameTag) return normalizeTag(seriesNameTag).slice(SERIES_NAME_PREFIX.length);
  return 'Community Newsletter';
}

export function isInternalNewsletterTag(tag: string): boolean {
  const normalized = normalizeTag(tag);
  return normalized.startsWith(SERIES_KEY_PREFIX) || normalized.startsWith(SERIES_NAME_PREFIX);
}

export function getLatestNewsletterIssues<T extends NewsletterSeriesPost>(posts: T[]): T[] {
  const latestBySeries = new Map<string, T>();

  for (const post of posts) {
    const seriesKey = getNewsletterSeriesKey(post);
    const current = latestBySeries.get(seriesKey);
    if (!current) {
      latestBySeries.set(seriesKey, post);
      continue;
    }

    const postTime = post.publishedAt ? new Date(post.publishedAt).getTime() : 0;
    const currentTime = current.publishedAt ? new Date(current.publishedAt).getTime() : 0;
    if (
      (post.isPinned && !current.isPinned) ||
      (post.isPinned === current.isPinned && postTime > currentTime)
    ) {
      latestBySeries.set(seriesKey, post);
    }
  }

  return [...latestBySeries.values()].sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    const aTime = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
    const bTime = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
    return bTime - aTime;
  });
}
